import { createSelector } from '@ngrx/store';
import {
  selectAccountFeatureCodes,
  selectFeatureCodes,
} from '../features/features.selectors';
import {
  selectActivePermissions,
  selectGlobalPermissions,
  selectIsAccountLevel,
} from '../user/user.selectors';
import { FEATURE_CODES } from '../models/feature.model';
import { SidebarMenuItem } from '../../../layout/sidebar/sidebar-menu-item.interface';
import { SIDEBAR_MENU_ITEMS } from '../../../layout/sidebar/sidebar-menu-items.const';
import { Permission } from '../../../core/auth/enums/permission.enum';

function hasPermission(permissions: Permission[], permission: Permission | undefined): boolean {
  if (!permission) {
    return true;
  }

  return permissions.includes(permission);
}

export const selectEffectiveSidebarPermissions = createSelector(
  selectActivePermissions,
  selectGlobalPermissions,
  selectIsAccountLevel,
  (activePermissions, globalPermissions, isAccountLevel): Permission[] => {
    if (isAccountLevel) {
      return globalPermissions;
    }

    return activePermissions.length > 0 ? activePermissions : globalPermissions;
  }
);

export const selectFilteredSidebarMenuItems = createSelector(
  selectFeatureCodes,
  selectAccountFeatureCodes,
  selectEffectiveSidebarPermissions,
  (
    featureCodes: string[],
    accountFeatureCodes: string[],
    permissions: Permission[]
  ): SidebarMenuItem[] => {
    return SIDEBAR_MENU_ITEMS
      .filter((item) => {
        if (!hasPermission(permissions, item.permission)) {
          return false;
        }

        if (item.id === 'power-pages') {
          return featureCodes.includes(FEATURE_CODES.POWER_PAGES);
        }

        return true;
      })
      .map((item) => {
        if (item.type === 'collapse' && item.children) {
          const filteredChildren = item.children.filter((child) => {
            if (!hasPermission(permissions, child.permission)) {
              return false;
            }

            if (child.id === 'trusted-destinations') {
              return accountFeatureCodes.includes(FEATURE_CODES.TRUSTED_DESTINATIONS);
            }

            if (child.id === 'pending-links') {
              return accountFeatureCodes.includes(FEATURE_CODES.PENDING_LINKS);
            }

            if (child.id === 'ip-exclusions') {
              return featureCodes.includes(FEATURE_CODES.IP_EXCLUSIONS);
            }

            if (child.id === 'e-link') {
              return featureCodes.includes(FEATURE_CODES.E_LINKS);
            }

            return true;
          });

          // Hide the Admin group entirely if all children were filtered out
          if (filteredChildren.length === 0) {
            return null;
          }

          return {
            ...item,
            children: filteredChildren,
          };
        }

        return item;
      })
      .filter((item): item is SidebarMenuItem => item !== null);
  }
);
